/**
 * Meta Client Plugin
 *
 * Handles client-side Meta (Facebook) Pixel tracking via fbq().
 * Fires events with proper event_id for server-side deduplication.
 *
 * IMPORTANT: Meta CAPI (server-side) will also fire the same event.
 * Deduplication is automatic via event_id matching.
 */

import { metaLog as log } from "../../logger";
import { shouldFireTo } from "../registry";
import type { EventName, PluginResult, UnifiedEvent } from "../types";
import { BasePlugin, isMetaStandardEvent } from "./base";

const FB_PIXEL_ID = "757665840533275";

// Meta event name mapping (internal → Meta standard)
// NOTE: Two different ad campaigns use Meta:
// 1. Applicant acquisition: Lead, SubmitApplication (standard events)
// 2. Employer acquisition: VerifyEmail (custom event for targeting employers)
const META_EVENT_MAPPING: Partial<Record<EventName, string>> = {
  // Lead stays as Lead (standard event for applicant-targeting ads)
  // VerifyEmail stays as VerifyEmail (custom event for employer-targeting ads)
  BeginCheckout: "InitiateCheckout", // Meta uses different name
  ViewJobListing: "ViewContent",
  ViewJobPreview: "ViewContent",
};

/**
 * Map internal event name to Meta event name
 */
function mapEventName(event: EventName): string {
  return META_EVENT_MAPPING[event] || event;
}

export class MetaClientPlugin extends BasePlugin {
  name = "meta-client";
  private initialized = false;

  async initialize(): Promise<void> {
    if (typeof window !== "undefined") {
      await this.waitFor(() => typeof window.fbq === "function", 5000);
      this.initialized = true;
    }
  }

  supports(event: EventName): boolean {
    return shouldFireTo(event, "meta-client");
  }

  isReady(): boolean {
    return (
      typeof window !== "undefined" &&
      typeof window.fbq === "function" &&
      this.initialized
    );
  }

  async track(event: UnifiedEvent): Promise<PluginResult> {
    if (!this.isReady()) {
      return this.error("fbq_not_ready");
    }

    try {
      const metaEventName = mapEventName(event.eventName);
      const isStandard = isMetaStandardEvent(metaEventName);

      // Re-init pixel with Advanced Matching user data if available
      const ctx = event.context;
      if (ctx) {
        const userData: Record<string, string> = {};
        if (ctx.hashedEmail) userData.em = ctx.hashedEmail;
        if (ctx.hashedPhone) userData.ph = ctx.hashedPhone;
        if (ctx.hashedFirstName) userData.fn = ctx.hashedFirstName;
        if (ctx.hashedLastName) userData.ln = ctx.hashedLastName;
        if (ctx.hashedCity) userData.ct = ctx.hashedCity;
        if (ctx.hashedZip) userData.zp = ctx.hashedZip;
        if (ctx.hashedCountry) userData.country = ctx.hashedCountry;
        if (ctx.hashedDob) userData.db = ctx.hashedDob;
        if (ctx.applicantId) userData.external_id = ctx.applicantId;

        if (Object.keys(userData).length > 0) {
          window.fbq!("init", FB_PIXEL_ID, userData);
        }
      }

      // Build Meta params (event data only — no PII)
      const metaParams: Record<string, unknown> = {
        ...event.params,

        // Content info
        content_name: event.params.job_title || event.params.content_name,
        content_category: event.params.category || "job_application",

        // Include UTM for reporting
        ...(event.attribution.utmSource && {
          utm_source: event.attribution.utmSource,
        }),
        ...(event.attribution.utmMedium && {
          utm_medium: event.attribution.utmMedium,
        }),
        ...(event.attribution.utmCampaign && {
          utm_campaign: event.attribution.utmCampaign,
        }),
      };

      // Fire with event_id for deduplication
      const fbqOptions = { eventID: event.eventId };

      if (isStandard) {
        window.fbq!("track", metaEventName, metaParams, fbqOptions);
      } else {
        window.fbq!("trackCustom", metaEventName, metaParams, fbqOptions);
      }

      log.info(`Tracked: ${metaEventName}`, {
        eventId: event.eventId,
        isStandard,
      });

      return this.success({ eventName: metaEventName, isStandard });
    } catch (err) {
      log.error("Tracking error", err);
      return this.error(err instanceof Error ? err.message : "unknown_error");
    }
  }
}
